import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { signatureService, companyService } from '../../api/services';

const Signature: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [tsaUrl, setTsaUrl] = useState('');
  const [hasKeystore, setHasKeystore] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    companyService.get().then((c: any) => {
      setHasKeystore(Boolean(c?.signatureKeystore) || Boolean(c?.signerDn));
      if (c?.signatureTsaUrl) setTsaUrl(c.signatureTsaUrl);
    }).catch(() => {});
  }, []);

  const upload = async () => {
    if (!file) { toast.error('Sélectionnez un fichier .p12'); return; }
    setBusy(true);
    try {
      await signatureService.upload(file, password, tsaUrl || undefined);
      setHasKeystore(true);
      setFile(null);
      setPassword('');
      toast.success('Certificat de signature chargé');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur — vérifiez le mot de passe et le format PKCS#12');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer le certificat de signature actuel ?')) return;
    setBusy(true);
    try {
      await signatureService.remove();
      setHasKeystore(false);
      toast.success('Certificat supprimé');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <h2 className="text-lg font-semibold">Signature électronique des PDFs</h2>
      <p className="text-sm text-gray-500">
        Uploadez votre certificat PKCS#12 (<code>.p12</code>) pour signer numériquement vos factures.
        Sans certificat, GreenBill utilise un certificat auto-signé de test (signature valide
        cryptographiquement mais non opposable).
      </p>

      {hasKeystore ? (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-900 space-y-2">
          <p>✓ Un certificat est actuellement chargé pour votre société.</p>
          <button onClick={remove} disabled={busy}
                  className="text-xs text-red-700 underline">Supprimer le certificat</button>
        </div>
      ) : (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
          Aucun certificat — vos PDFs signés porteront la mention "self-signed (non-qualified)".
        </div>
      )}

      <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Fichier PKCS#12 (.p12 / .pfx)</label>
          <input type="file" accept=".p12,.pfx,application/x-pkcs12"
                 onChange={(e) => setFile(e.target.files?.[0] || null)}
                 className="text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Mot de passe du keystore</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                 className="w-full rounded-lg border border-gray-200 px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">URL du service TSA (optionnel, RFC 3161)</label>
          <input type="url" value={tsaUrl} onChange={(e) => setTsaUrl(e.target.value)}
                 placeholder="https://tsa.example.tn"
                 className="w-full rounded-lg border border-gray-200 px-3 py-2" />
          <p className="text-xs text-gray-500 mt-1">
            Quand configuré, un horodatage qualifié sera embedded dans la signature (PAdES-B-T).
          </p>
        </div>
        <button onClick={upload} disabled={busy || !file}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
          {busy ? 'Upload…' : 'Charger le certificat'}
        </button>
      </div>
    </div>
  );
};

export default Signature;
